import { capitalize, ordinalize } from 'inflected';
import { ActualVsPredictedRow, CPTBRow, ROASRow, SaturationRow } from '../dtc-commerce-mmm-types';
import { TagTone, TagToneType } from './channel-insights-constants';
import {
  ABOVE_WEIGHT_MIN_RATIO,
  BELOW_WEIGHT_MAX_RATIO,
  BREAK_EVEN_RETURN,
  BUSIER_MIN_INDEX,
  ComparisonMode,
  ComparisonModeType,
  DARK_LEAD_WEEKS,
  DARK_MIN_SPEND_SHARE,
  DARK_TAIL_WEEKS,
  DAYS_PER_YEAR_SHIFT,
  DIMINISHING_MAX_RATIO,
  HighlightCard,
  HIGHLY_SEASONAL_MIN_RATIO,
  HighlightChip,
  LAUNCH_MIN_LIVE_SHARE,
  LAUNCH_MIN_SIDE_RATIO,
  MAX_CARDS_PER_CHIP,
  MAX_DARK_FEATURES_PER_CARD,
  MAX_PORTFOLIO_CARDS,
  MAX_SAME_RANGE_WEEKS,
  MIN_CHANNEL_SPEND_SHARE,
  MIN_COMPARE_WEEKS,
  MIN_RANGE_WEEKS,
  MIN_SHARE_OF_TYPICAL,
  MIN_WEEKLY_SPEND,
  ModelHighlightsResult,
  PEAK_MONTH_MIN_WEEKS,
  PORTFOLIO_SLIGHT_MAX_CHANGE,
  PORTFOLIO_STEADY_MAX_CHANGE,
  QUIETER_MAX_INDEX,
  RETURN_TREND_MIN_CHANGE,
  ROOM_TO_SCALE_MIN_RATIO,
  SAME_SPEND_MAX_CHANGE,
  SEASONAL_PEAK_MIN_INDEX,
  SEASONALITY_MIN_HISTORY_WEEKS,
  SPEND_SHIFT_MIN_CHANGE,
  SPIKE_MIN_HISTORY_WEEKS,
  SPIKE_MIN_RATIO,
  SUGGESTION_END_WITHIN_WEEKS,
  SUGGESTION_WEEKS,
  TOTAL_HIGHLIGHT_CARDS,
  WEEKS_PER_YEAR
} from './model-highlights-constants';
import {
  formatDollarsPrecise,
  hasConfidenceBands,
  interpCurve,
  returnRangeAt
} from './reallocation-v4-utils';

// The six Model Highlights cards for whatever weeks the date picker selects. Every rule reads the
// selected weeks, except seasonality (always the full history) and suggestions (always the latest
// SUGGESTION_WEEKS model weeks, so advice doesn't change with the dates).

const MONTH_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

// ---- Formatting ----

const percent = (value: number): string => `${Math.round(Math.abs(value) * 100)}%`;

const dollars = (value: number): string => `$${value.toFixed(2)}`;

// Under 3× one decimal ("1.3×"), from 3× whole ("3×").
const multiple = (ratio: number): string =>
  ratio < 3 ? `${ratio.toFixed(1)}×` : `${Math.round(ratio)}×`;

const shareText = (share: number): string => {
  if (share < 0.01) return 'under 1%';
  if (share < 0.1) return `${(share * 100).toFixed(1).replace(/\.0$/, '')}%`;
  return `${Math.round(share * 100)}%`;
};

const listText = (items: string[]): string => {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

// In a sentence that lists several channels, each name's own commas move into parentheses so
// the list still reads: "Paid Social, Meta, Traffic" becomes "Paid Social (Meta, Traffic)".
export const listedChannelName = (displayName: string): string => {
  const comma = displayName.indexOf(', ');
  if (comma < 0) return displayName;
  return `${displayName.slice(0, comma)} (${displayName.slice(comma + 2)})`;
};

const monthOf = (date: string): number => Number(date.slice(5, 7)) - 1;

// Full month name from a 'YYYY-MM-DD' date or a 'YYYY-MM' month key: "November".
export const monthName = (date: string): string => MONTH_LONG[monthOf(date)];

// Dates read with full month names and ordinal days: "May 18th" or "May 18th, 2026".
export const dayLabel = (date: string, withYear = true): string => {
  const base = `${monthName(date)} ${ordinalize(Number(date.slice(8, 10)))}`;
  return withYear ? `${base}, ${date.slice(0, 4)}` : base;
};

// "March 2026–May 2026" or "June 2025–May 2026" (a span always names the year on both ends),
// "November 2025" for one month; without years, "March–May" or "November".
export const monthSpanLabel = (weeks: string[], withYear = true): string => {
  const first = weeks[0];
  const last = weeks[weeks.length - 1];
  const a = monthName(first);
  const b = monthName(last);
  const sameMonth = a === b && first.slice(0, 4) === last.slice(0, 4);
  if (!withYear) return sameMonth ? a : `${a}–${b}`;
  if (sameMonth) return `${a} ${last.slice(0, 4)}`;
  return `${a} ${first.slice(0, 4)}–${b} ${last.slice(0, 4)}`;
};

export const shiftDate = (date: string, days: number): string => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const median = (values: number[]): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const sum = (values: number[]): number => values.reduce((s, v) => s + v, 0);

const chip = (label: string, tone: TagToneType): HighlightChip => ({ label, tone });

// ---- Data ----

type WeeklySeries = Map<string, Map<string, number>>;

interface ModelSeries {
  weeks: string[];
  weekIndex: Map<string, number>;
  names: string[];
  demandCapture: Set<string>;
  tested: Set<string>;
  // Per feature, per week: effective spend, raw spend and attributed results.
  spend: WeeklySeries;
  rawSpend: WeeklySeries;
  results: WeeklySeries;
  // All-feature totals per week.
  weeklySpend: Map<string, number>;
  weeklyResults: Map<string, number>;
  curves: Map<string, SaturationRow[]>;
  isRevenue: boolean;
}

const addTo = (series: WeeklySeries, name: string, date: string, value: number) => {
  const byWeek = series.get(name) ?? new Map<string, number>();
  byWeek.set(date, (byWeek.get(date) ?? 0) + value);
  series.set(name, byWeek);
};

export const buildModelSeries = (
  cptb: CPTBRow[],
  roas: ROASRow[],
  saturation: SaturationRow[],
  isRevenue: boolean
): ModelSeries => {
  const weeks = [...new Set(cptb.map(r => r.date))].sort();
  const spend: WeeklySeries = new Map();
  const rawSpend: WeeklySeries = new Map();
  const results: WeeklySeries = new Map();
  const weeklySpend = new Map<string, number>();
  const weeklyResults = new Map<string, number>();
  cptb.forEach(r => {
    addTo(spend, r.name, r.date, r.effectiveSpend);
    addTo(rawSpend, r.name, r.date, r.rawSpend);
    addTo(results, r.name, r.date, r.effectiveNormalized);
    weeklySpend.set(r.date, (weeklySpend.get(r.date) ?? 0) + r.effectiveSpend);
    weeklyResults.set(r.date, (weeklyResults.get(r.date) ?? 0) + r.effectiveNormalized);
  });
  const curves = new Map<string, SaturationRow[]>();
  saturation.forEach(p => curves.set(p.name, [...(curves.get(p.name) ?? []), p]));
  curves.forEach(points => points.sort((a, b) => a.adstockSpend - b.adstockSpend));
  return {
    weeks,
    weekIndex: new Map(weeks.map((w, i) => [w, i])),
    names: roas.map(r => r.name),
    demandCapture: new Set(roas.filter(r => r.mediaType === 'demand_capture').map(r => r.name)),
    tested: new Set(roas.filter(r => r.incrementalityTest).map(r => r.name)),
    spend,
    rawSpend,
    results,
    weeklySpend,
    weeklyResults,
    curves,
    isRevenue
  };
};

const total = (series: WeeklySeries, name: string, weeks: string[]): number => {
  const byWeek = series.get(name);
  if (!byWeek) return 0;
  return weeks.reduce((s, w) => s + (byWeek.get(w) ?? 0), 0);
};

const portfolioTotal = (m: ModelSeries, series: WeeklySeries, weeks: string[]): number =>
  sum(m.names.map(n => total(series, n, weeks)));

// A week (or a weekly average) only counts as real spend when it clears a dollar and 1% of typical.
const isRealSpend = (weekly: number, typical: number): boolean =>
  weekly >= MIN_WEEKLY_SPEND && weekly >= typical * MIN_SHARE_OF_TYPICAL;

// ---- Windows ----

interface HighlightWindows {
  // The selected weeks: the same rows the headline tiles sum.
  range: string[];
  // The weeks compared, and the weeks they are compared with; empty when there's no comparison.
  compared: string[];
  baseline: string[];
  mode: ComparisonModeType;
  // How many years back the baseline sits; 0 when there's no comparison.
  yearsBack: number;
  // The 52 weeks ending at the range end: what a "typical" or "normal" week means.
  trailingYear: string[];
  // The latest SUGGESTION_WEEKS model weeks: what suggestions read.
  latest: string[];
  // The range ends within SUGGESTION_END_WITHIN_WEEKS of the latest model week: restart advice and
  // suggestions only show then.
  endsAtLatest: boolean;
}

export const buildWindows = (m: ModelSeries, range: string[]): HighlightWindows => {
  const endIndex = range.length ? (m.weekIndex.get(range[range.length - 1]) ?? 0) : 0;
  const trailingYear = m.weeks.slice(Math.max(0, endIndex - (WEEKS_PER_YEAR - 1)), endIndex + 1);
  const latest = m.weeks.slice(-SUGGESTION_WEEKS);
  type Comparison = Pick<HighlightWindows, 'compared' | 'baseline' | 'mode' | 'yearsBack'>;
  const none: Comparison = { compared: [], baseline: [], mode: ComparisonMode.NONE, yearsBack: 0 };
  const pick = (): Comparison => {
    if (range.length < MIN_COMPARE_WEEKS) return none;
    if (range.length <= MAX_SAME_RANGE_WEEKS) {
      return {
        compared: range,
        baseline: range.map(d => shiftDate(d, -DAYS_PER_YEAR_SHIFT)),
        mode: ComparisonMode.SAME_WEEKS,
        yearsBack: 1
      };
    }
    // Long ranges compare the latest year with the same weeks as many whole years back as the
    // range reaches (at least one), so both sides cover the same calendar months.
    const lastYear = range.slice(-WEEKS_PER_YEAR);
    let yearsBack = 1;
    while (shiftDate(lastYear[0], -DAYS_PER_YEAR_SHIFT * (yearsBack + 1)) >= range[0]) {
      yearsBack += 1;
    }
    return {
      compared: lastYear,
      baseline: lastYear.map(d => shiftDate(d, -DAYS_PER_YEAR_SHIFT * yearsBack)),
      mode: yearsBack > 1 ? ComparisonMode.FIRST_VS_LATEST : ComparisonMode.LATEST_YEAR,
      yearsBack
    };
  };
  const picked = pick();
  // Every year-earlier week must be in the data, or nothing is compared.
  const comparison = picked.baseline.every(d => m.weekIndex.has(d)) ? picked : none;
  const endsAtLatest =
    range[range.length - 1] >= m.weeks[m.weeks.length - SUGGESTION_END_WITHIN_WEEKS];
  return { range, ...comparison, trailingYear, latest, endsAtLatest };
};

// The baseline named by the compared weeks' own months, whole years back. 52 weeks is 364 days, so
// each baseline week starts a day or two later on the calendar, and naming it by its own date can
// slip into the next month: the week of August 31st, 2025 lines up with September 1st, 2024.
const baselineLabel = (w: HighlightWindows): string => {
  const yearsEarlier = (date: string) =>
    `${Number(date.slice(0, 4)) - w.yearsBack}${date.slice(4)}`;
  return monthSpanLabel([
    yearsEarlier(w.compared[0]),
    yearsEarlier(w.compared[w.compared.length - 1])
  ]);
};

const comparedWithText = (w: HighlightWindows): string => {
  if (w.mode === ComparisonMode.SAME_WEEKS) return 'vs the same weeks a year earlier';
  if (w.mode === ComparisonMode.FIRST_VS_LATEST) {
    return `in ${monthSpanLabel(w.compared)} vs ${baselineLabel(w)}`;
  }
  return `in ${monthSpanLabel(w.compared)} vs a year earlier`;
};

// ---- Portfolio: year-over-year efficiency ----

// Reads better or worse rather than up or down, so one set of chips works for revenue and for
// cost per conversion: a higher return per dollar is a lower cost per conversion.
const portfolioTrendChip = (change: number): HighlightChip => {
  const size = Math.abs(change);
  const better = change > 0;
  if (size < PORTFOLIO_STEADY_MAX_CHANGE) return chip('Holding steady', TagTone.NEUTRAL);
  if (size < PORTFOLIO_SLIGHT_MAX_CHANGE) {
    return better
      ? chip('Slightly improving', TagTone.POSITIVE)
      : chip('Slightly declining', TagTone.CAUTION);
  }
  return better ? chip('Improving', TagTone.POSITIVE) : chip('Declining', TagTone.NEGATIVE);
};

const buildYearOverYearCard = (m: ModelSeries, w: HighlightWindows): HighlightCard | null => {
  const { compared, baseline, mode } = w;
  if (compared.length === 0) return null;
  const spendNow = portfolioTotal(m, m.spend, compared);
  const spendThen = portfolioTotal(m, m.spend, baseline);
  const resultsNow = portfolioTotal(m, m.results, compared);
  const resultsThen = portfolioTotal(m, m.results, baseline);
  if (spendNow <= 0 || spendThen <= 0 || resultsNow <= 0 || resultsThen <= 0) return null;
  const returnNow = resultsNow / spendNow;
  const returnThen = resultsThen / spendThen;
  const change = returnNow / returnThen - 1;
  // The spend wording uses raw spend, the same field as the Media spend tile.
  const rawNow = portfolioTotal(m, m.rawSpend, compared) / compared.length;
  const rawThen = portfolioTotal(m, m.rawSpend, baseline) / baseline.length;
  const spendChange = rawThen > 0 ? rawNow / rawThen - 1 : 0;
  const spendText =
    Math.abs(spendChange) < SAME_SPEND_MAX_CHANGE
      ? 'on about the same spend'
      : `while spend ${spendChange > 0 ? 'grew' : 'fell'} ${percent(spendChange)}`;

  let lead = 'Paid media';
  let thenText = 'the same weeks a year earlier';
  let context = `vs ${baselineLabel(w)}`;
  if (mode !== ComparisonMode.SAME_WEEKS) {
    lead = `In ${monthSpanLabel(compared)}, paid media`;
    thenText = mode === ComparisonMode.FIRST_VS_LATEST ? baselineLabel(w) : 'the year before';
    context = `Latest year vs ${baselineLabel(w)}`;
  }

  // Conversions read as cost per conversion, so "cheaper" is the good direction.
  const { isRevenue } = m;
  const valueNow = isRevenue
    ? `${dollars(returnNow)} per dollar`
    : `${dollars(1 / returnNow)} per conversion`;
  const valueThen = dollars(isRevenue ? returnThen : 1 / returnThen);
  const verb = isRevenue ? 'returned' : 'cost';
  const shown = isRevenue ? change : returnThen / returnNow - 1;
  const size = Math.abs(change);
  let headline: string;
  if (size >= PORTFOLIO_SLIGHT_MAX_CHANGE) {
    headline = `${lead} ${verb} ${valueNow}, ${percent(shown)} ${shown > 0 ? 'more' : 'less'} than ${thenText} (${valueThen}), ${spendText}.`;
  } else if (size >= PORTFOLIO_STEADY_MAX_CHANGE) {
    headline = `${lead} ${verb} ${valueNow}, ${shown > 0 ? 'up' : 'down'} ${percent(shown)} from ${valueThen} in ${thenText}, ${spendText}.`;
  } else {
    headline = `${lead} ${verb} ${valueNow}, about the same as ${valueThen} in ${thenText}, ${spendText}.`;
  }
  return {
    id: 'year-over-year',
    featureName: undefined,
    title: 'Year-over-year efficiency',
    context,
    headline,
    chip: portfolioTrendChip(change),
    suggestion: undefined
  };
};

// ---- Portfolio: seasonality (always from the full history) ----

// Days in each calendar month of a non-leap year, against the average month's 365 / 12.
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const AVERAGE_MONTH_DAYS = 365 / 12;

// Each calendar month's results as a multiple of an average month across the full history. It
// starts from each month's average week, so a part-month at either edge of the data still counts
// fairly, then scales by the month's length: a 28-day February brings in less than a 31-day March.
const seasonalIndex = (actualVsPredicted: ActualVsPredictedRow[]): number[] | null => {
  if (actualVsPredicted.length < SEASONALITY_MIN_HISTORY_WEEKS) return null;
  const overall = sum(actualVsPredicted.map(r => r.actualKpi)) / actualVsPredicted.length;
  if (overall <= 0) return null;
  const byMonth: number[][] = Array.from({ length: 12 }, () => []);
  actualVsPredicted.forEach(r => byMonth[monthOf(r.date)].push(r.actualKpi));
  return byMonth.map((values, month) =>
    values.length
      ? (sum(values) / values.length / overall) * (DAYS_IN_MONTH[month] / AVERAGE_MONTH_DAYS)
      : 1
  );
};

interface SeasonalContext {
  index: number[] | null;
  peaks: Set<number>;
  historyLabel: string;
}

const buildSeasonalContext = (actualVsPredicted: ActualVsPredictedRow[]): SeasonalContext => {
  const index = seasonalIndex(actualVsPredicted);
  const dates = actualVsPredicted.map(r => r.date).sort();
  const firstYear = dates.length ? dates[0].slice(0, 4) : '';
  const lastYear = dates.length ? dates[dates.length - 1].slice(0, 4) : '';
  return {
    index,
    peaks: new Set(
      (index ?? []).flatMap((v, month) => (v >= SEASONAL_PEAK_MIN_INDEX ? [month] : []))
    ),
    historyLabel: `${firstYear}–${lastYear} history`
  };
};

const buildSeasonalityCard = (
  m: ModelSeries,
  w: HighlightWindows,
  s: SeasonalContext
): HighlightCard | null => {
  const { index, peaks } = s;
  if (!index) return null;
  const { range } = w;
  const { isRevenue } = m;
  const noun = isRevenue ? 'Revenue' : 'Conversions';
  const verb = (singular: string, plural: string) => (isRevenue ? singular : plural);
  // Busy months run at least SEASONAL_PEAK_MIN_INDEX; only the biggest one is "the peak".
  const busyMonths = [...peaks].sort((a, b) => index[b] - index[a]);
  const [top] = busyMonths;
  const quietest = index.reduce((low, v, month) => (v < index[low] ? month : low), 0);
  const peakSentence =
    top === undefined
      ? ''
      : `${noun} ${verb('peaks', 'peak')} in ${MONTH_LONG[top]}, at about ${multiple(index[top])} an average month.`;
  const runsAbout = (month: number) =>
    `${MONTH_LONG[month]} usually runs about ${multiple(index[month])} an average month`;
  const card = (headline: string, seasonChip: HighlightChip): HighlightCard => ({
    id: 'seasonality',
    featureName: undefined,
    title: 'Seasonality',
    context: s.historyLabel,
    headline,
    chip: seasonChip,
    suggestion: undefined
  });

  // A year or more covers every season, so the card describes the whole pattern.
  if (range.length >= WEEKS_PER_YEAR) {
    if (top === undefined) {
      const busiest = index.reduce((high, v, month) => (v > index[high] ? month : high), 0);
      return card(
        `${noun} ${verb('stays', 'stay')} close to an average month all year, never above about ${multiple(index[busiest])}.`,
        chip('Steady year-round', TagTone.NEUTRAL)
      );
    }
    const highly = index[top] >= HIGHLY_SEASONAL_MIN_RATIO * index[quietest];
    return card(
      `${noun} ${verb('peaks', 'peak')} in ${MONTH_LONG[top]} at about ${multiple(index[top])} an average month, and ${verb('is', 'are')} quietest in ${MONTH_LONG[quietest]} at about ${multiple(index[quietest])}.`,
      highly ? chip('Highly seasonal', TagTone.BRAND) : chip('Mildly seasonal', TagTone.NEUTRAL)
    );
  }

  // Shorter ranges: the dates decide which months the card talks about.
  const weeksPerMonth = new Map<number, number>();
  range.forEach(d => weeksPerMonth.set(monthOf(d), (weeksPerMonth.get(monthOf(d)) ?? 0) + 1));
  const inRange = (month: number) => (weeksPerMonth.get(month) ?? 0) >= PEAK_MONTH_MIN_WEEKS;
  if (top !== undefined && inRange(top)) {
    return card(
      `These dates include the seasonal peak: ${runsAbout(top)}.`,
      chip('Peak season', TagTone.BRAND)
    );
  }
  const busyInRange = busyMonths.filter(inRange);
  if (busyInRange.length) {
    const lead =
      busyInRange.length === 1
        ? 'These dates include a busy month'
        : 'These dates include busy months';
    return card(
      `${lead}: ${listText(busyInRange.map(runsAbout))}. ${peakSentence}`,
      chip('Busier stretch', TagTone.NEUTRAL)
    );
  }
  const rangeIndex = sum(range.map(d => index[monthOf(d)])) / range.length;
  const label = monthSpanLabel(range, false);
  const withPeak = (text: string) => [text, peakSentence].filter(Boolean).join(' ');
  if (rangeIndex <= QUIETER_MAX_INDEX) {
    return card(
      withPeak(
        `${label} is a quieter stretch: ${noun.toLowerCase()} usually ${verb('runs', 'run')} about ${Math.round((1 - rangeIndex) * 100)}% below an average month.`
      ),
      chip('Quieter stretch', TagTone.NEUTRAL)
    );
  }
  if (rangeIndex >= BUSIER_MIN_INDEX) {
    return card(
      withPeak(
        `${label} usually runs about ${Math.round((rangeIndex - 1) * 100)}% above an average month.`
      ),
      chip('Busier stretch', TagTone.NEUTRAL)
    );
  }
  return card(
    withPeak(`${label} usually runs close to an average month.`),
    chip('Typical stretch', TagTone.NEUTRAL)
  );
};

// ---- Portfolio: went dark, or the standout week ----

interface DarkFeature {
  name: string;
  weeklyBefore: number;
  lastWeek: string;
  restartPays: boolean;
}

// The model's low-end read on the first dollars back. Demand capture never gets restart advice,
// since its spend follows demand rather than creating it.
const restartPays = (m: ModelSeries, name: string): boolean => {
  if (m.demandCapture.has(name)) return false;
  const curve = m.curves.get(name) ?? [];
  const firstPoint = curve.find(p => p.adstockSpend > 0);
  if (!firstPoint || !hasConfidenceBands(curve)) return false;
  return interpCurve(curve, firstPoint.adstockSpend, 'ciLoReturnNextDollar') >= BREAK_EVEN_RETURN;
};

// Spent in the lead weeks before the range's last weeks, and close to nothing in those last weeks.
// Small channels sit out so a minor stop never takes a top slot.
const findDarkFeatures = (m: ModelSeries, w: HighlightWindows): DarkFeature[] => {
  const { range } = w;
  const tail = range.slice(-DARK_TAIL_WEEKS);
  if (tail.length === 0) return [];
  const tailStart = m.weekIndex.get(tail[0]) ?? 0;
  const lead = m.weeks.slice(Math.max(0, tailStart - DARK_LEAD_WEEKS), tailStart);
  if (lead.length < DARK_LEAD_WEEKS) return [];
  const leadTotal = sum(lead.map(d => m.weeklySpend.get(d) ?? 0));
  if (leadTotal <= 0) return [];
  const rangeEnd = range[range.length - 1];
  return m.names
    .flatMap(name => {
      const leadSpend = total(m.spend, name, lead);
      if (leadSpend / leadTotal < DARK_MIN_SPEND_SHARE) return [];
      const weeklyBefore = leadSpend / lead.length;
      if (isRealSpend(total(m.spend, name, tail) / tail.length, weeklyBefore)) return [];
      const byWeek = m.spend.get(name);
      const spent = m.weeks.filter(
        d => d <= rangeEnd && (byWeek?.get(d) ?? 0) >= MIN_SHARE_OF_TYPICAL * weeklyBefore
      );
      return [
        {
          name,
          weeklyBefore,
          lastWeek: spent[spent.length - 1] ?? lead[lead.length - 1],
          restartPays: restartPays(m, name)
        }
      ];
    })
    .sort((a, b) => b.weeklyBefore - a.weeklyBefore);
};

export const buildWentDarkCard = (
  m: ModelSeries,
  w: HighlightWindows,
  dark: DarkFeature[],
  displayNameFor: (name: string) => string
): HighlightCard | null => {
  if (dark.length === 0) return null;
  const shown = dark.slice(0, MAX_DARK_FEATURES_PER_CARD);
  const months = [...new Set(shown.map(d => monthOf(d.lastWeek)))]
    .sort((a, b) => a - b)
    .map(month => MONTH_LONG[month]);
  const nameFor = (name: string) =>
    shown.length === 1 ? displayNameFor(name) : listedChannelName(displayNameFor(name));
  const names = listText(shown.map(d => nameFor(d.name)));
  let headline =
    shown.length === 1
      ? `${names} stopped spending in ${monthName(shown[0].lastWeek)}. It had been running about ${formatDollarsPrecise(shown[0].weeklyBefore)} a week.`
      : `${names} stopped spending in ${listText(months)}, after about ${formatDollarsPrecise(sum(shown.map(d => d.weeklyBefore)))} a week between them.`;
  // Restarting "pays for itself" is a break-even claim, so it's revenue-only.
  const worthRestarting = shown.filter(d => d.restartPays);
  if (m.isRevenue && w.endsAtLatest && worthRestarting.length) {
    headline +=
      shown.length === 1
        ? ' The model expects a restart to pay for itself.'
        : ` The model expects restarting ${listText(worthRestarting.map(d => nameFor(d.name)))} to pay for itself.`;
  }
  return {
    id: 'went-dark',
    featureName: undefined,
    title: 'Went dark',
    context:
      shown.length === 1
        ? `Last spend: week of ${dayLabel(shown[0].lastWeek, false)}`
        : shown.map(d => displayNameFor(d.name)).join(' | '),
    contextFeatures: shown.length === 1 ? undefined : shown.map(d => d.name),
    headline,
    chip: chip('Paused', TagTone.NEUTRAL),
    suggestion: undefined
  };
};

interface WeekMultiple {
  date: string;
  // Media-driven results and paid spend as multiples of a normal (median) week.
  results: number;
  spend: number;
}

// Every week in the range against a normal week of the trailing year, or null when there isn't
// enough history to say what normal is.
const weeklyMultiples = (m: ModelSeries, w: HighlightWindows): WeekMultiple[] | null => {
  const { range, trailingYear } = w;
  if (trailingYear.length < SPIKE_MIN_HISTORY_WEEKS || range.length === 0) return null;
  const normalOf = (series: Map<string, number>) =>
    median(trailingYear.map(d => series.get(d) ?? 0).filter(v => v > 0));
  const normalResults = normalOf(m.weeklyResults);
  const normalSpend = normalOf(m.weeklySpend);
  if (normalResults <= 0 || normalSpend <= 0) return null;
  return range.map(d => ({
    date: d,
    results: (m.weeklyResults.get(d) ?? 0) / normalResults,
    spend: (m.weeklySpend.get(d) ?? 0) / normalSpend
  }));
};

const biggestWeek = (weeks: WeekMultiple[], by: 'results' | 'spend'): WeekMultiple =>
  weeks.reduce((best, x) => (x[by] > best[by] ? x : best));

const mediaResultsNoun = (isRevenue: boolean): string =>
  isRevenue ? 'media-driven revenue' : 'media-driven conversions';

// The range's standout week. It leads with the biggest media-driven results week, and adds the
// spend peak when that fell in a different week, since the heaviest spend week isn't always the
// best one. Busy months count too: seasonality names months, this card names weeks.
const buildSpikeCard = (m: ModelSeries, w: HighlightWindows): HighlightCard | null => {
  const weeks = weeklyMultiples(m, w);
  if (!weeks) return null;
  const resultsPeak = biggestWeek(weeks, 'results');
  const spendPeak = biggestWeek(weeks, 'spend');
  // With no results standout, a spend-only spike still gets the card, led by its own week.
  let lead = resultsPeak;
  if (resultsPeak.results < SPIKE_MIN_RATIO) {
    if (spendPeak.spend < SPIKE_MIN_RATIO) return null;
    lead = spendPeak;
  }
  const week = dayLabel(lead.date, false);
  // Both numbers always show, so neither side is ever summed up as "close to normal".
  const sentences = [
    `The week of ${week} brought in ${lead.results.toFixed(1)}× a normal week's ${mediaResultsNoun(m.isRevenue)} on ${lead.spend.toFixed(1)}× a normal week's spend.`
  ];
  if (spendPeak.date !== lead.date && spendPeak.spend >= SPIKE_MIN_RATIO) {
    sentences.push(
      `Spend peaked the week of ${dayLabel(spendPeak.date, false)}, at ${spendPeak.spend.toFixed(1)}× a normal week.`
    );
  }
  return {
    id: 'spike',
    featureName: undefined,
    title: 'Standout week',
    context: `Week of ${week}`,
    headline: sentences.join(' '),
    chip: chip('Outlier', TagTone.BRAND),
    suggestion: undefined
  };
};

// When no week stands out, this fills a slot the channel cards couldn't, so the grid stays full.
// It still says something true: the client's weeks ran evenly.
const buildConsistentWeeksCard = (m: ModelSeries, w: HighlightWindows): HighlightCard | null => {
  const weeks = weeklyMultiples(m, w);
  if (!weeks) return null;
  const busiest = biggestWeek(weeks, 'results');
  const week = dayLabel(busiest.date, false);
  return {
    id: 'consistent-weeks',
    featureName: undefined,
    title: 'Standout week',
    context: `Week of ${week}`,
    headline: `No week ran more than ${SPIKE_MIN_RATIO}× a normal week. The busiest was the week of ${week}, at ${busiest.results.toFixed(1)}× a normal week's ${mediaResultsNoun(m.isRevenue)}.`,
    chip: chip('Consistent', TagTone.NEUTRAL),
    suggestion: undefined
  };
};

// ---- Channels ----

interface CurveRead {
  // The next-dollar return at the channel's average weekly spend over the weeks read.
  nextDollar: number;
  // The plausible range of that next-dollar return, when the curve has bands.
  range: { low: number; high: number } | null;
}

// Reads the curve once, at the channel's average weekly spend over the weeks: the same average the
// card shows at the top right, so "each additional dollar" means one more dollar at that level.
const readCurve = (m: ModelSeries, name: string, weeks: string[]): CurveRead | null => {
  const curve = m.curves.get(name) ?? [];
  if (curve.length === 0 || weeks.length === 0) return null;
  const averageSpend = total(m.spend, name, weeks) / weeks.length;
  if (averageSpend < MIN_WEEKLY_SPEND) return null;
  const { expected, low, high } = returnRangeAt(curve, averageSpend);
  return { nextDollar: expected, range: hasConfidenceBands(curve) ? { low, high } : null };
};

interface ChannelFacts {
  name: string;
  isDemandCapture: boolean;
  isTested: boolean;
  weeklySpend: number;
  spendShare: number;
  resultShare: number;
  // Results per dollar it actually earned over the range.
  realizedReturn: number | null;
  read: CurveRead | null;
  latestRead: CurveRead | null;
  // Results per dollar over the latest weeks, which suggestions read.
  latestReturn: number;
  latestBelowWeight: boolean;
  spendChange: number | null;
  returnNow: number | null;
  returnThen: number | null;
}

// A comparison only counts once the channel has really run on both sides: no launches, no stops.
const isComparable = (
  m: ModelSeries,
  name: string,
  w: HighlightWindows,
  typical: number
): boolean => {
  const { compared, baseline } = w;
  if (compared.length === 0) return false;
  const now = total(m.spend, name, compared) / compared.length;
  const then = total(m.spend, name, baseline) / baseline.length;
  if (!isRealSpend(now, typical) || !isRealSpend(then, typical)) return false;
  if (Math.min(now, then) / Math.max(now, then) < LAUNCH_MIN_SIDE_RATIO) return false;
  const byWeek = m.spend.get(name);
  const liveWeeks = baseline.filter(d => (byWeek?.get(d) ?? 0) >= MIN_WEEKLY_SPEND).length;
  return liveWeeks >= LAUNCH_MIN_LIVE_SHARE * baseline.length;
};

export const channelFacts = (
  m: ModelSeries,
  name: string,
  w: HighlightWindows,
  portfolioSpend: number,
  portfolioResults: number
): ChannelFacts => {
  const { range, trailingYear, latest } = w;
  const typical = total(m.spend, name, trailingYear) / trailingYear.length;
  const rangeSpend = total(m.spend, name, range);
  const rangeResults = total(m.results, name, range);
  const weeklySpend = rangeSpend / range.length;
  const latestSpend = total(m.spend, name, latest);
  const latestResults = total(m.results, name, latest);
  const latestPortfolioSpend = portfolioTotal(m, m.spend, latest);
  const latestPortfolioResults = portfolioTotal(m, m.results, latest);
  const latestSpendShare = latestPortfolioSpend > 0 ? latestSpend / latestPortfolioSpend : 0;
  const latestResultShare = latestPortfolioResults > 0 ? latestResults / latestPortfolioResults : 0;
  const facts: ChannelFacts = {
    name,
    isDemandCapture: m.demandCapture.has(name),
    isTested: m.tested.has(name),
    weeklySpend,
    spendShare: portfolioSpend > 0 ? rangeSpend / portfolioSpend : 0,
    resultShare: portfolioResults > 0 ? rangeResults / portfolioResults : 0,
    realizedReturn: rangeSpend > 0 ? rangeResults / rangeSpend : null,
    read: isRealSpend(weeklySpend, typical) ? readCurve(m, name, range) : null,
    latestRead: readCurve(m, name, latest),
    latestReturn: latestSpend > 0 ? latestResults / latestSpend : 0,
    // Backs a pullback suggestion: below its weight over the latest weeks, not just the range.
    latestBelowWeight:
      latestSpendShare >= MIN_CHANNEL_SPEND_SHARE &&
      latestResultShare / latestSpendShare <= BELOW_WEIGHT_MAX_RATIO,
    spendChange: null,
    returnNow: null,
    returnThen: null
  };
  if (!isComparable(m, name, w, typical)) return facts;
  const { compared, baseline } = w;
  const spendNow = total(m.spend, name, compared);
  const spendThen = total(m.spend, name, baseline);
  return {
    ...facts,
    spendChange: spendNow / compared.length / (spendThen / baseline.length) - 1,
    returnNow: total(m.results, name, compared) / spendNow,
    returnThen: total(m.results, name, baseline) / spendThen
  };
};

// What a channel card can lead with. The key drives the headline; the chip is what shows.
export const SignalKey = {
  TREND: 'trend',
  DIMINISHING: 'diminishing',
  ROOM: 'room',
  WEIGHT: 'weight'
} as const;
type SignalKeyType = (typeof SignalKey)[keyof typeof SignalKey];

interface Signal {
  key: SignalKeyType;
  chip: HighlightChip;
  // Which way the signal points, for checking a suggestion doesn't contradict it.
  direction: 'up' | 'down';
}

const DIMINISHING_SIGNAL: Signal = {
  key: SignalKey.DIMINISHING,
  chip: chip('Diminishing returns', TagTone.CAUTION),
  direction: 'down'
};
const ROOM_SIGNAL: Signal = {
  key: SignalKey.ROOM,
  chip: chip('Room to scale', TagTone.POSITIVE),
  direction: 'up'
};
const ABOVE_WEIGHT_SIGNAL: Signal = {
  key: SignalKey.WEIGHT,
  chip: chip('Above its weight', TagTone.POSITIVE),
  direction: 'up'
};
const BELOW_WEIGHT_SIGNAL: Signal = {
  key: SignalKey.WEIGHT,
  chip: chip('Below its weight', TagTone.NEGATIVE),
  direction: 'down'
};
const IMPROVING_SIGNAL: Signal = {
  key: SignalKey.TREND,
  chip: chip('Improving', TagTone.POSITIVE),
  direction: 'up'
};
const DECLINING_SIGNAL: Signal = {
  key: SignalKey.TREND,
  chip: chip('Declining', TagTone.NEGATIVE),
  direction: 'down'
};

const returnTrendChange = (f: ChannelFacts): number | null =>
  f.returnNow !== null && f.returnThen !== null && f.returnNow > 0 && f.returnThen > 0
    ? f.returnNow / f.returnThen - 1
    : null;

// Room to scale needs a demand-generation channel and, for revenue, a next dollar that breaks even:
// rules about what the channel is, not bars to clear, so a near miss never loosens them.
const canHaveRoom = (f: ChannelFacts, isRevenue: boolean): boolean =>
  !f.isDemandCapture && (!isRevenue || (f.read?.nextDollar ?? 0) >= BREAK_EVEN_RETURN);

interface NearMiss {
  signal: Signal;
  // How far short of the rule's bar the channel falls, as a share of the bar: 0 when it clears it.
  gap: number;
}

// How close a channel comes to every rule a channel card can lead with.
const nearMisses = (f: ChannelFacts, isRevenue: boolean): NearMiss[] => {
  // Bars a value must reach, and bars it must stay at or under.
  const shortOf = (value: number, bar: number) => (value > 0 ? Math.max(0, bar / value - 1) : null);
  const overBy = (value: number, bar: number) => Math.max(0, value / bar - 1);
  const misses: { signal: Signal; gap: number | null }[] = [];
  const { read, realizedReturn } = f;
  if (read && realizedReturn && realizedReturn > 0) {
    const ratio = read.nextDollar / realizedReturn;
    misses.push({ signal: DIMINISHING_SIGNAL, gap: overBy(ratio, DIMINISHING_MAX_RATIO) });
    if (canHaveRoom(f, isRevenue)) {
      misses.push({ signal: ROOM_SIGNAL, gap: shortOf(ratio, ROOM_TO_SCALE_MIN_RATIO) });
    }
  }
  if (f.spendShare >= MIN_CHANNEL_SPEND_SHARE) {
    const weight = f.resultShare / f.spendShare;
    misses.push({ signal: ABOVE_WEIGHT_SIGNAL, gap: shortOf(weight, ABOVE_WEIGHT_MIN_RATIO) });
    misses.push({ signal: BELOW_WEIGHT_SIGNAL, gap: overBy(weight, BELOW_WEIGHT_MAX_RATIO) });
  }
  const change = returnTrendChange(f);
  if (change !== null) {
    misses.push({
      signal: change > 0 ? IMPROVING_SIGNAL : DECLINING_SIGNAL,
      gap: shortOf(Math.abs(change), RETURN_TREND_MIN_CHANGE)
    });
  }
  return misses.flatMap(({ signal, gap }) => (gap === null ? [] : [{ signal, gap }]));
};

// The rules a channel clears outright: its near misses with no gap.
const channelSignals = (f: ChannelFacts, isRevenue: boolean): Signal[] =>
  nearMisses(f, isRevenue)
    .filter(miss => miss.gap === 0)
    .map(miss => miss.signal);

const isGoodNews = (s: Signal): boolean => s.direction === 'up';

// Which signal a card leads with: a ROAS change that came with a big spend change, then diminishing
// returns, then the good news (room to scale, above its weight, improving), then below its weight,
// then a declining ROAS on its own.
const leadOptions = (f: ChannelFacts, signals: Signal[]): Signal[] => {
  const trendLeads = f.spendChange !== null && Math.abs(f.spendChange) >= SPEND_SHIFT_MIN_CHANGE;
  const rank = (s: Signal): number => {
    if (s.key === SignalKey.TREND && trendLeads) return 0;
    if (s.key === SignalKey.DIMINISHING) return 1;
    if (s.key === SignalKey.ROOM) return 2;
    if (s.key === SignalKey.WEIGHT && isGoodNews(s)) return 3;
    if (s.key === SignalKey.TREND && isGoodNews(s)) return 4;
    if (s.key === SignalKey.WEIGHT) return 5;
    return 6;
  };
  return [...signals].sort((a, b) => rank(a) - rank(b));
};

const ratioText = (ratio: number): string => {
  if (ratio < 0.45) return 'less than half';
  if (ratio <= 0.55) return 'about half';
  return `${percent(ratio)} of`;
};

export const channelHeadline = (
  f: ChannelFacts,
  signal: Signal,
  w: HighlightWindows,
  isRevenue: boolean
): string => {
  const nextDollar = f.read?.nextDollar ?? 0;
  const realized = f.realizedReturn ?? 0;
  if (signal.key === SignalKey.DIMINISHING) {
    if (isRevenue) {
      return `Each additional dollar brings back ${dollars(nextDollar)}, ${ratioText(nextDollar / realized)} the ${dollars(realized)} it averaged per dollar over these dates.`;
    }
    return nextDollar > 0
      ? `Each additional conversion costs ${dollars(1 / nextDollar)}, against the ${dollars(1 / realized)} it averaged over these dates.`
      : `Additional spend brings back no further conversions, against the ${dollars(1 / realized)} per conversion it averaged over these dates.`;
  }
  if (signal.key === SignalKey.ROOM) {
    // A channel still ramping up can earn more on the next dollar than it has averaged. Under the
    // room-to-scale bar (only a near miss filling an empty slot), the gap reads as a number rather
    // than "close to".
    const ratio = nextDollar / realized;
    let revenueGap = 'close to';
    let costGap = 'close to';
    if (nextDollar > realized) {
      revenueGap = 'more than';
      costGap = 'less than';
    } else if (ratio < ROOM_TO_SCALE_MIN_RATIO) {
      revenueGap = `${percent(ratio)} of`;
      costGap = `${percent(1 / ratio - 1)} more than`;
    }
    return isRevenue
      ? `Each additional dollar still brings back ${dollars(nextDollar)}, ${revenueGap} the ${dollars(realized)} it averaged per dollar over these dates.`
      : `Each additional conversion still costs ${dollars(1 / nextDollar)}, ${costGap} the ${dollars(1 / realized)} it averaged over these dates.`;
  }
  if (signal.key === SignalKey.WEIGHT) {
    return `Drives ${shareText(f.resultShare)} of ${mediaResultsNoun(isRevenue)} on ${shareText(f.spendShare)} of spend, about ${multiple(f.resultShare / f.spendShare)} its share.`;
  }
  const now = f.returnNow ?? 0;
  const then = f.returnThen ?? 0;
  const change = now / then - 1;
  const trend = isRevenue
    ? `ROAS ${change > 0 ? 'rose' : 'fell'} ${percent(change)} ${comparedWithText(w)} (${dollars(then)} → ${dollars(now)})`
    : `CPA ${then / now - 1 > 0 ? 'rose' : 'fell'} ${percent(then / now - 1)} ${comparedWithText(w)} (${dollars(1 / then)} → ${dollars(1 / now)})`;
  const spendChange = f.spendChange ?? 0;
  if (Math.abs(spendChange) < SPEND_SHIFT_MIN_CHANGE) return `${capitalize(trend)}.`;
  const spend =
    spendChange > 0
      ? `Spend grew ${spendChange >= 1 ? multiple(1 + spendChange) : percent(spendChange)}`
      : `Spend was cut ${percent(spendChange)}`;
  return `${spend} and ${trend}.`;
};

const SuggestionKind = {
  PULLBACK: 'pullback',
  HOLDOUT: 'holdout',
  INCREASE: 'increase'
} as const;
type SuggestionKindType = (typeof SuggestionKind)[keyof typeof SuggestionKind];

const SUGGESTION_TEXT: Record<SuggestionKindType, string> = {
  [SuggestionKind.PULLBACK]: 'Suggested: test a pullback',
  [SuggestionKind.HOLDOUT]: 'Suggested: holdout test',
  [SuggestionKind.INCREASE]: 'Suggested: test a spend increase'
};

// Suggestions read the latest weeks only, appear only when the range runs up to the latest
// results, never go to demand capture, and never contradict the card's chip.
const channelSuggestion = (
  f: ChannelFacts,
  signal: Signal,
  w: HighlightWindows,
  m: ModelSeries,
  // Conversions: the plausible next dollar must straddle the typical channel's, not break-even.
  holdoutLine: number
): string | undefined => {
  if (f.isDemandCapture || !f.latestRead || !w.endsAtLatest) return undefined;
  const { nextDollar, range } = f.latestRead;
  const ratio = f.latestReturn > 0 ? nextDollar / f.latestReturn : null;
  let kind: SuggestionKindType | undefined;
  if (
    (!m.isRevenue || nextDollar < BREAK_EVEN_RETURN) &&
    ratio !== null &&
    ratio <= DIMINISHING_MAX_RATIO &&
    f.latestBelowWeight
  ) {
    kind = SuggestionKind.PULLBACK;
  } else if (!f.isTested && range && range.low < holdoutLine && holdoutLine < range.high) {
    kind = SuggestionKind.HOLDOUT;
  } else if (
    ratio !== null &&
    ratio >= ROOM_TO_SCALE_MIN_RATIO &&
    (!m.isRevenue || nextDollar >= BREAK_EVEN_RETURN)
  ) {
    kind = SuggestionKind.INCREASE;
  }
  if (!kind) return undefined;
  if (kind === SuggestionKind.PULLBACK && signal.direction === 'up') return undefined;
  if (kind === SuggestionKind.INCREASE && signal.direction === 'down') return undefined;
  return SUGGESTION_TEXT[kind];
};

interface Candidate {
  facts: ChannelFacts;
  options: Signal[];
}

interface Picked {
  facts: ChannelFacts;
  signal: Signal;
}

// Ranks by weekly spend in the range and caps how many cards share a chip. When none of the picks
// carries good news, the last slot goes to the biggest channel that has some, leading with it.
const pickChannelCards = (pool: Candidate[], slots: number): Picked[] => {
  const used = new Map<string, number>();
  const count = (label: string) => used.get(label) ?? 0;
  const firstFit = (c: Candidate) => c.options.find(s => count(s.chip.label) < MAX_CARDS_PER_CHIP);
  const picked: Picked[] = [];
  pool.forEach(c => {
    if (picked.length === slots) return;
    const signal = firstFit(c);
    if (!signal) return;
    picked.push({ facts: c.facts, signal });
    used.set(signal.chip.label, count(signal.chip.label) + 1);
  });
  if (slots === 0 || picked.some(p => isGoodNews(p.signal))) return picked;
  const shown = new Set(picked.map(p => p.facts.name));
  const candidate = pool.find(c => !shown.has(c.facts.name) && c.options.some(isGoodNews));
  // No good-news chip is in use yet, so the candidate's always fits under the chip cap.
  const signal = candidate?.options.find(isGoodNews);
  if (!candidate || !signal) return picked;
  if (picked.length === slots) {
    picked.pop();
  }
  picked.push({ facts: candidate.facts, signal });
  return picked;
};

// Fills the slots the rules left empty. The bars loosen, and each slot goes to the leftover channel
// closest to clearing a rule, leading with that rule's own sentence and chip. The chip cap doesn't
// apply: moving to a further-off rule would stretch the card more than repeating a chip does.
const pickNearMissCards = (
  leftover: ChannelFacts[],
  slots: number,
  isRevenue: boolean
): Picked[] => {
  if (slots <= 0) return [];
  const closest = (facts: ChannelFacts): NearMiss | undefined => {
    const misses = nearMisses(facts, isRevenue);
    // Equally close rules fall back on the usual lead order.
    const order = leadOptions(
      facts,
      misses.map(miss => miss.signal)
    );
    return [...misses].sort(
      (a, b) => a.gap - b.gap || order.indexOf(a.signal) - order.indexOf(b.signal)
    )[0];
  };
  return (
    leftover
      .flatMap(facts => {
        const miss = closest(facts);
        return miss ? [{ facts, ...miss }] : [];
      })
      // Closest first; equally close goes to the bigger channel, since leftover is ranked by spend.
      .sort((a, b) => a.gap - b.gap)
      .slice(0, slots)
      .map(({ facts, signal }) => ({ facts, signal }))
  );
};

// ---- Assembly ----

interface BuildModelHighlightsParams {
  cptb: CPTBRow[];
  roas: ROASRow[];
  saturation: SaturationRow[];
  actualVsPredicted: ActualVsPredictedRow[];
  startDate: string;
  endDate: string;
  isRevenue: boolean;
  displayNameFor: (name: string) => string;
}

export const buildModelHighlights = ({
  cptb,
  roas,
  saturation,
  actualVsPredicted,
  startDate,
  endDate,
  isRevenue,
  displayNameFor
}: BuildModelHighlightsParams): ModelHighlightsResult => {
  const m = buildModelSeries(cptb, roas, saturation, isRevenue);
  const range = m.weeks.filter(d => (!startDate || d >= startDate) && (!endDate || d <= endDate));
  const w = buildWindows(m, range);
  if (range.length < MIN_RANGE_WEEKS) {
    return { portfolio: [], channels: [], comparison: w.mode, tooShort: true };
  }

  const seasonal = buildSeasonalContext(actualVsPredicted);
  const dark = findDarkFeatures(m, w);
  const wentDark = buildWentDarkCard(m, w, dark, displayNameFor);
  const spike = buildSpikeCard(m, w);
  const portfolio = [
    buildYearOverYearCard(m, w),
    buildSeasonalityCard(m, w, seasonal),
    wentDark,
    spike
  ]
    .filter((card): card is HighlightCard => card !== null)
    .slice(0, MAX_PORTFOLIO_CARDS);
  // Channels on the went-dark card don't also get their own card.
  const darkNames = new Set(
    portfolio.some(c => c.id === 'went-dark')
      ? dark.slice(0, MAX_DARK_FEATURES_PER_CARD).map(d => d.name)
      : []
  );

  const portfolioSpend = portfolioTotal(m, m.spend, range);
  const portfolioResults = portfolioTotal(m, m.results, range);
  // Every channel big enough for a card, ranked by weekly spend.
  const eligible = m.names
    .filter(n => !darkNames.has(n))
    .map(n => channelFacts(m, n, w, portfolioSpend, portfolioResults))
    .filter(f => f.spendShare >= MIN_CHANNEL_SPEND_SHARE)
    .sort((a, b) => b.weeklySpend - a.weeklySpend);
  const pool: Candidate[] = eligible.flatMap(facts => {
    const options = leadOptions(facts, channelSignals(facts, isRevenue));
    return options.length ? [{ facts, options }] : [];
  });

  // Conversions: a holdout needs the plausible next dollar to straddle the typical channel's. When
  // no channel clears a rule, every channel big enough for a card sets that line instead, so near
  // misses filling the grid don't read against a line of zero.
  const latestNextDollars = (facts: ChannelFacts[]) =>
    facts.flatMap(f => (f.latestRead ? [f.latestRead.nextDollar] : []));
  const poolNextDollars = latestNextDollars(pool.map(c => c.facts));
  const holdoutLine = isRevenue
    ? BREAK_EVEN_RETURN
    : median(poolNextDollars.length ? poolNextDollars : latestNextDollars(eligible));
  const picked = pickChannelCards(pool, TOTAL_HIGHLIGHT_CARDS - portfolio.length);
  // No standout week and too few channel cards to fill the grid: the even-weeks card fills it.
  const isShort = portfolio.length + picked.length < TOTAL_HIGHLIGHT_CARDS;
  const consistent = !spike && isShort ? buildConsistentWeeksCard(m, w) : null;
  // Any slot still open goes to the channel closest to clearing a rule.
  const shown = new Set(picked.map(p => p.facts.name));
  const nearMissCards = pickNearMissCards(
    eligible.filter(f => !shown.has(f.name)),
    TOTAL_HIGHLIGHT_CARDS - portfolio.length - (consistent ? 1 : 0) - picked.length,
    isRevenue
  );
  const channels = [...picked, ...nearMissCards].map(({ facts, signal }): HighlightCard => ({
    id: `channel-${facts.name}`,
    featureName: facts.name,
    title: displayNameFor(facts.name),
    context: `Avg ${formatDollarsPrecise(facts.weeklySpend)}/wk`,
    headline: channelHeadline(facts, signal, w, isRevenue),
    chip: signal.chip,
    suggestion: channelSuggestion(facts, signal, w, m, holdoutLine)
  }));
  return {
    portfolio: consistent ? [...portfolio, consistent] : portfolio,
    channels,
    comparison: w.mode,
    tooShort: false
  };
};
